/**
 * Pure helpers over {@link AgentPrices}: one record's price for an agent a
 * play or a run names, from the per-agent prices the BE estimate returns.
 * A price that hasn't loaded is `undefined`, so callers hide it instead of
 * showing a guess.
 */

import type { AgentPrices, AgentScope } from '@/types';
import {
  EMPLOYMENT_CHECK_MAX_CREDITS,
  EMPLOYMENT_CHECK_SKILL_ID,
} from '@/utils/employment-check.util';
import { WRITE_CREDITS, WRITE_SKILL_ID } from '@/utils/write-skill.util';

/** Play agent ids: `builtin:research` (both scopes, as a play runs it on a
 *  person; its company scope on a company), `builtin:qualification`, or a
 *  saved custom agent's id. Employment check and Write are flat priced
 *  builtin skills, so they have a price before the estimate loads. */
export function agentPricePerRecord(
  prices: AgentPrices | undefined,
  agentId: string,
  recordKind: 'person' | 'company' = 'person'
): number | undefined {
  if (agentId === EMPLOYMENT_CHECK_SKILL_ID)
    return EMPLOYMENT_CHECK_MAX_CREDITS;
  if (agentId === WRITE_SKILL_ID) return WRITE_CREDITS;
  if (!prices) return undefined;
  if (agentId === 'builtin:research')
    return recordKind === 'company'
      ? prices.research.company
      : prices.research.both;
  if (agentId === 'builtin:qualification') return prices.qualification;
  return prices.customAgents[agentId];
}

/** The agents' prices summed for one record; `undefined` until every one of
 *  them has a price. No agents cost nothing. */
export function agentsCostPerRecord(
  agentIds: readonly string[],
  prices: AgentPrices | undefined,
  recordKind: 'person' | 'company' = 'person'
): number | undefined {
  let total = 0;
  for (const id of agentIds) {
    const price = agentPricePerRecord(prices, id, recordKind);
    if (price === undefined) return undefined;
    total += price;
  }
  return total;
}

/** `7`, `0.5`: a credit amount without trailing zeros. */
export function formatAgentCredits(credits: number): string {
  return Number.isInteger(credits)
    ? String(credits)
    : String(+credits.toFixed(2));
}

/** `7–18` across research scopes, or one number when they agree. */
export function formatCreditRange(values: readonly number[]): string {
  const low = Math.min(...values);
  const high = Math.max(...values);
  return low === high
    ? formatAgentCredits(low)
    : `${formatAgentCredits(low)}–${formatAgentCredits(high)}`;
}

/** `Company 17.8 · Contact 6.3 · Both 12.2`: research's price per scope, each
 *  named, since a scope's price doesn't follow its depth. */
export function formatResearchScopePrices(
  research: Record<AgentScope, number>
): string {
  return (['company', 'contact', 'both'] as const)
    .map(
      (scope) =>
        `${scope[0].toUpperCase()}${scope.slice(1)} ${formatAgentCredits(research[scope])}`
    )
    .join(' · ');
}

/** What an agent run over `rows` records is charged: the per-record price
 *  (a tenth of a credit with per-agent pricing) times the rows, to a tenth
 *  of a credit, as the BE charges it (`credits_for_count`). Rounded to the
 *  tenth so float noise (0.7 x 3) never shows as 2.0999. */
export function agentRunCredits(perRecord: number, rows: number): number {
  return Math.round(perRecord * rows * 10) / 10;
}
